// App Builder's operations layer: the status strip every tab carries once an
// app is more than a draft, the Operate tab (instance health, releases,
// security, conversations, review queue, QA, sync history), the app map
// (agents → knowledge → data → surfaces → hosted instance, health-coloured),
// and the improvement loop (suggestions derived from live signals, each
// pointing at the tab where the fix happens). One read:
// GET /apps-builder/:orgId/:appId/operate (shared/appOperate.js).
import { LAB_PHASES } from '../data/consoleCatalog';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ModuleFrame, { Chip, KV } from './ModuleFrame';
import { withApp } from './AppSelector';
import { createEntity, friendlyError, useEntityList } from '../data/entities';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { HealthSection, SecuritySection, CostsSection, DevAccessSection, EcosystemSection, ApSection, fmtAgo } from '../pages/org/AppsModule';

export function useOperate(orgId, appId, { days = 7, every = 60_000 } = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const reload = useCallback(() => {
    if (!orgId || !appId || !isBackendConfigured()) return Promise.resolve();
    return apiGet(`/apps-builder/${orgId}/${appId}/operate?days=${days}`)
      .then((d) => { setData(d); setErr(''); })
      .catch((e) => setErr(e.message));
  }, [orgId, appId, days]);
  useEffect(() => {
    setData(null);
    reload();
    const tick = () => { if (document.visibilityState === 'visible') reload(); };
    const t = setInterval(tick, every);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [reload, every]);
  return { data, err, reload };
}

const stateTone = (m) => (m?.state === 'up' ? 'ok' : m?.state === 'down' ? 'down' : m?.state ? 'warn' : 'off');
// The box as a whole: the outside-in probe, downgraded by any service the
// kit reports as not running (the probe can't see minio or mail die).
const instTone = (inst) => {
  const t = stateTone(inst?.monitor);
  if (t === 'ok' && inst?.services?.down?.length) return 'warn';
  return t;
};
const servicesLabel = (svc) => (!svc ? '' : svc.stale ? ' · services unknown' : ` · ${svc.up}/${svc.total} services`);
// Two kit refs name the same build when one is a prefix of the other (short vs full sha, or a tag both sides spell the same).
// One-line meta for the Phone section header (kit monitor.telephony).
function phoneMeta(t) {
  if (!t.enabled) return 'module off';
  const bits = [t.ready ? 'Nova answering' : 'not ready', `${t.active || 0} live`];
  if (t.draining) bits.push('draining');
  if (!t.transferConfigured) bits.push('no transfer target');
  return bits.join(' · ');
}

// The phone helpdesk on this instance: readiness the kit phones home and the
// month's outcome counters priced from the price book (GET
// /apps/:org/:app/telephony). The calls themselves live on the app (/calls).
function PhoneSection({ orgId, appId, monitor }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (document.visibilityState !== 'visible') return;
      apiGet(`/apps/${orgId}/${appId}/telephony`).then((d) => { if (alive) { setData(d); setErr(''); } }).catch((e) => { if (alive) setErr(e.message || 'unavailable'); });
    };
    load();
    const t = setInterval(load, 60000);
    document.addEventListener('visibilitychange', load);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', load); };
  }, [orgId, appId]);
  const usd = (c) => `$${(Number(c || 0) / 100).toFixed(2)}`;
  const flags = [
    ['Twilio keys', monitor.twilio], ['Model key', monitor.provider], ['Transfer target', monitor.transferConfigured], ['Failover number', monitor.failoverConfigured],
  ];
  return (
    <div data-nova-note="The phone helpdesk on this instance: whether Nova is answering (keys, transfer target, failover), live calls right now, and this month's billable outcomes — one verdict per ended call, priced per the price book. Open the app's /calls for transcripts.">
      <div className="ab-verdicts">
        <span className="ab-verdict"><Chip tone={monitor.ready ? 'ok' : monitor.enabled ? 'warn' : 'muted'}>{monitor.ready ? 'answering' : monitor.enabled ? 'not ready' : 'off'}</Chip><span className="mf-muted">{monitor.active || 0} live call{monitor.active === 1 ? '' : 's'}{monitor.draining ? ' · draining' : ''} · reported {fmtAgo(monitor.at)}</span></span>
        {flags.map(([k, v]) => <span key={k} className="ab-verdict"><Chip tone={v ? 'ok' : 'warn'}>{k}</Chip><span className="mf-muted">{v ? 'set' : 'missing'}</span></span>)}
      </div>
      {err && <p className="cs-help">{err}</p>}
      {data && (
        <>
          <p className="cs-help" style={{ marginTop: 8 }}>
            <strong>{data.totals.outcomes}</strong> billable outcome{data.totals.outcomes === 1 ? '' : 's'} this month · <strong>{usd(data.totals.amountCents)}</strong> · {data.totals.answered} answered · {data.totals.failedOver} failed over
          </p>
          <ul className="mf-list">
            {data.rows.filter((r) => r.count || r.rateCents).map((r) => (
              <li key={r.key} className="mf-row">
                <span className="mf-row-grow">{r.label}</span>
                <span className="mf-muted">{r.count} {r.unit}{r.count === 1 ? '' : 's'}{r.rateCents ? ` × ${usd(r.rateCents)} = ${usd(r.amountCents)}` : ''}</span>
              </li>
            ))}
          </ul>
          {data.urls && (
            <p className="cs-help">
              <a className="cs-btn cs-btn-sm" href={data.urls.calls} target="_blank" rel="noreferrer">Open calls on the app</a>
              <a className="cs-btn cs-btn-sm" href={data.urls.analytics} target="_blank" rel="noreferrer" style={{ marginLeft: 6 }}>Analytics + CSV</a>
            </p>
          )}
        </>
      )}
    </div>
  );
}

// One-line meta for the Brain section header.
function brainMeta(b) {
  const parts = [];
  if (b.appPages) parts.push(`${b.appPages} app page${b.appPages === 1 ? '' : 's'}`);
  else parts.push(`${b.lessons} lesson${b.lessons === 1 ? '' : 's'} · ${b.decisions} decision${b.decisions === 1 ? '' : 's'}`);
  if (b.kitPages) parts.push(`${b.kitPages} kit pages`);
  if (b.verdicts) {
    const bad = ['populated', 'updating', 'current'].filter((k) => b.verdicts[k] && !b.verdicts[k].ok);
    parts.push(bad.length ? `needs attention: ${bad.join(', ')}` : 'populated · updating · current');
  }
  return parts.join(' · ');
}
const sameRef = (a, b) => { const x = String(a || ''); const y = String(b || ''); return !!x && !!y && (x.startsWith(y) || y.startsWith(x)); };
const LEVEL_TONE = { ok: 'ok', warn: 'warn', down: 'down', off: 'off' };

// ---- Updates (kit release policy) ---------------------------------------------------
// GET/POST /apps/:orgId/:appId/updates (shared/kitUpdatePolicy.js): auto-update
// on/off, the maintenance window a non-urgent release waits for, a hold, and
// "Update now" (a reconcile release for this instance alone, window ignored).
const updatesMeta = (inst) => (inst?.updates?.auto === false ? 'auto-update off' : 'auto-update on · kit releases land in the maintenance window');
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hh = (h) => `${String(h).padStart(2, '0')}:00`;
const TZS = ['America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'];

export function UpdatesSection({ orgId, appId, isAdmin }) {
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [win, setWin] = useState(null); // editing copy of the own window
  const load = useCallback(() => apiGet(`/apps/${orgId}/${appId}/updates`).then((v) => { setView(v); setErr(''); }).catch((e) => setErr(e.message)), [orgId, appId]);
  useEffect(() => { load(); }, [load]);
  const post = async (label, path, body) => {
    setBusy(label);
    try { const v = await apiPost(path, body); if (v?.policy) setView(v); else await load(); setErr(''); } catch (e) { setErr(e.message); } finally { setBusy(''); }
  };
  if (err && !view) return <p className="cs-help" role="alert">{err}</p>;
  if (!view) return <p className="cs-help">Loading…</p>;
  const { policy, decision, kit, release, swap } = view;
  const tz = win?.tz || policy.window?.tz || TZS[0];
  const behindTone = kit.kitTrack ? 'info' : kit.behind ? 'warn' : kit.running ? 'ok' : 'muted';
  return (
    <div className="ab-updates" data-nova-note="Kit updates: the instance runs `running`; the fleet's latest gated release is `latest`. Auto-update on means every kit release lands here inside the maintenance window; Update now stamps the latest release immediately. 'Updates without downtime' means a second app tier is staged and verified before the edge flips to it (no restart of the running app). An org admin changes the policy.">
      <div className="ab-verdicts is-compact" style={{ marginBottom: 8 }}>
        <span className="ab-verdict"><Chip tone={behindTone}>{kit.kitTrack ? `tracks ${kit.kitTrack}` : kit.behind ? 'behind' : kit.running ? 'current' : 'no report yet'}</Chip><span className="mf-muted">running {kit.running || '—'}{kit.latest && kit.latest !== kit.running ? ` · latest ${kit.latest}` : ''}</span></span>
        <span className="ab-verdict"><Chip tone={policy.auto ? 'ok' : 'warn'}>{policy.auto ? 'auto-update on' : 'auto-update off'}</Chip><span className="mf-muted">window {policy.windowLabel}{policy.ownWindow ? '' : ' (fleet default)'}</span></span>
        {swap?.split && (
          <span className="ab-verdict">
            <Chip tone={swap.lastSwap?.flippedBack ? 'warn' : 'ok'}>{swap.swapping ? 'updating now' : 'updates without downtime'}</Chip>
            <span className="mf-muted">
              {swap.swapping ? `staging ${swap.swapping.ref || 'the new kit'} beside the running app` : swap.lastSwap ? `last update ${new Date(swap.lastSwap.at).toLocaleString()}${swap.lastSwap.gapMs != null ? ` · ${swap.lastSwap.gapMs} ms edge gap` : ''}${swap.lastSwap.flippedBack ? ' · rolled back' : ''}` : 'the running app is never restarted to update'}
            </span>
          </span>
        )}
        {policy.holdUntil > Date.now() && <span className="ab-verdict"><Chip tone="warn">held</Chip><span className="mf-muted">until {new Date(policy.holdUntil).toLocaleString()}{policy.note ? ` · ${policy.note}` : ''}</span></span>}
        {kit.rollFailures > 0 && <span className="ab-verdict"><Chip tone="danger">{kit.rollFailures} failed update{kit.rollFailures === 1 ? '' : 's'}</Chip><span className="mf-muted">{kit.rollError || 'see the release log'}</span></span>}
      </div>
      {release && (
        <p className="cs-help" style={{ marginTop: 0 }}>
          A kit release is {release.status} ({release.ref}{release.stage ? ` · ${release.stage}` : ''}{release.auto ? ' · automatic' : release.reconcile ? ' · catch-up' : ''}).
          {release.deferredHere ? ` This instance is deferred: ${release.deferredHere.reason === 'window' ? `it updates when the window opens${release.deferredHere.until ? ` (${new Date(release.deferredHere.until).toLocaleString()})` : ''}` : release.deferredHere.reason}.` : ''}
        </p>
      )}
      {!release && kit.behind && !kit.kitTrack && (
        <p className="cs-help" style={{ marginTop: 0 }}>
          {decision.ok ? 'This instance is behind and eligible — the next catch-up sweep (every 30 min) updates it.'
            : decision.reason === 'window' ? `This instance updates when its window opens${decision.until ? ` (${new Date(decision.until).toLocaleString()})` : ''}.`
            : decision.reason === 'auto-off' ? 'Auto-update is off — this instance stays on its kit until someone presses Update now.'
            : decision.reason === 'hold' ? 'Updates are on hold for this instance.'
            : decision.reason === 'failures' ? 'Updates stopped after repeated failures — an incident is open; Update now retries once.' : ''}
        </p>
      )}
      {isAdmin && (
        <div className="ab-updates-controls" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy} onClick={() => post('auto', `/apps/${orgId}/${appId}/updates`, { auto: !policy.auto })}>
            {policy.auto ? 'Turn auto-update off' : 'Turn auto-update on'}
          </button>
          {policy.holdUntil > Date.now()
            ? <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy} onClick={() => post('hold', `/apps/${orgId}/${appId}/updates`, { holdUntil: null, note: '' })}>Release hold</button>
            : <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy} onClick={() => post('hold', `/apps/${orgId}/${appId}/updates`, { holdUntil: Date.now() + 7 * 86400e3, note: 'held for 7 days from Operate' })}>Hold updates 7 days</button>}
          <button type="button" className="cs-btn cs-btn-sm" disabled={!!busy || !!release || !kit.latest || (!kit.behind && !!kit.running) || !!kit.kitTrack} data-agent-guard="reprovisions the instance now" title={release ? 'a release is already running' : !kit.behind && kit.running ? 'already on the latest release' : `stamp ${kit.latest || ''} now, ignoring the window`}
            onClick={() => post('now', `/apps/${orgId}/${appId}/update-now`, {})}>
            {busy === 'now' ? 'Starting…' : 'Update now'}
          </button>
          <span className="mf-muted" style={{ flexBasis: '100%' }} />
          <label className="mf-muted">Window
            <select className="cs-input" value={win?.startHour ?? policy.window?.startHour ?? ''} onChange={(e) => setWin({ ...(win || policy.window || { endHour: 6 }), startHour: Number(e.target.value), tz })}>
              {HOURS.map((h) => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
            –
            <select className="cs-input" value={win?.endHour ?? policy.window?.endHour ?? ''} onChange={(e) => setWin({ ...(win || policy.window || { startHour: 22 }), endHour: Number(e.target.value), tz })}>
              {HOURS.map((h) => <option key={h} value={h}>{hh(h)}</option>)}
            </select>
            <select className="cs-input" value={tz} onChange={(e) => setWin({ ...(win || policy.window || { startHour: 22, endHour: 6 }), tz: e.target.value })}>
              {[...new Set([tz, ...TZS])].map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </label>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" disabled={!!busy || !win} onClick={() => post('window', `/apps/${orgId}/${appId}/updates`, { window: win }).then(() => setWin(null))}>Save window</button>
          {policy.ownWindow && <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy} onClick={() => post('window', `/apps/${orgId}/${appId}/updates`, { window: 'default' }).then(() => setWin(null))}>Use fleet default</button>}
          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={!!busy || policy.window === null} title="update at any hour" onClick={() => post('window', `/apps/${orgId}/${appId}/updates`, { window: null }).then(() => setWin(null))}>No window</button>
        </div>
      )}
      {err && <p className="cs-help" role="alert">{err}</p>}
    </div>
  );
}

// ---- Status strip -----------------------------------------------------------------
export function AppStatusStrip({ org, app, data, tab, onTab }) {
  if (!data) return null;
  const inst = data.instance;
  const lastDeploy = (data.deploys || []).find((d) => !(d.targets || []).includes('app-bundle')) || null;
  const sync = data.sync;
  const conv = data.conversations || {};
  const counts = inst?.scan?.counts || {};
  const open = ['critical', 'high', 'medium'].filter((k) => counts[k]);
  const urgent = (data.suggestions || []).filter((s) => s.severity === 'critical' || s.severity === 'high');
  return (
    <div className="ab-strip" role="region" aria-label="App status">
      <div className="ab-strip-cells">
        <StripCell k="Hosted app" tone={inst ? (inst.status === 'live' ? instTone(inst) : 'warn') : 'off'}
          v={inst ? (inst.url ? <a href={inst.url} target="_blank" rel="noreferrer">{inst.slug}</a> : inst.slug) : 'not linked'}
          sub={inst ? `${inst.status}${inst.monitor?.state ? ` · ${inst.monitor.state}${inst.monitor.lastLatencyMs != null ? ` ${inst.monitor.lastLatencyMs}ms` : ''}` : ''}${servicesLabel(inst.services)}` : 'link one under Ready to ship'} />
        {inst && (
          <StripCell k="Last release" tone={lastDeploy ? (lastDeploy.status === 'done' ? 'ok' : lastDeploy.status === 'error' ? 'down' : 'warn') : 'off'}
            v={lastDeploy ? `${lastDeploy.env} · ${lastDeploy.headSha || lastDeploy.ref || lastDeploy.status}` : '—'}
            sub={lastDeploy ? `${lastDeploy.status} · ${fmtAgo(lastDeploy.at)}` : 'no deploys yet'} />
        )}
        {inst && (
          <StripCell k="Design sync" tone={!sync ? 'warn' : sync.drift ? 'warn' : 'ok'}
            v={!sync ? 'never' : sync.drift ? 'behind' : 'in sync'}
            sub={sync ? `${sync.agents} agent · ${sync.docs} docs · ${fmtAgo(sync.at)}` : 'Surfaces → Sync'} />
        )}
        <StripCell k={`Conversations · ${conv.days || 7}d`} tone={conv.sessions ? (conv.unanswered ? 'warn' : 'ok') : 'off'}
          v={conv.sessions ? `${conv.sessions}` : '0'}
          sub={conv.sessions ? `${conv.turns} turns · ${conv.unanswered} unanswered` : data.agents?.length ? 'no traffic yet' : 'no agent'} />
        <StripCell k="Review queue" tone={data.review?.pending ? (data.review.pending > 5 ? 'down' : 'warn') : 'ok'}
          v={`${data.review?.pending || 0} waiting`}
          sub={data.review?.pending ? 'Human Review' : data.review?.decided ? `${data.review.decided} decided this week` : 'nothing pending'} />
        {data.spend && (
          <StripCell k={`Model spend · ${data.spend.period}`} tone={data.spend.credits?.exhausted ? 'down' : data.spend.credits && !data.spend.credits.unlimited && data.spend.credits.remainingUsd < 5 ? 'warn' : data.spend.usd ? 'ok' : 'off'}
            v={data.spend.usd ? `$${data.spend.usd.toFixed(2)}` : '$0.00'}
            sub={data.spend.credits?.exhausted ? 'credits exhausted — hosted calls paused' : data.spend.credits && !data.spend.credits.unlimited ? `$${data.spend.credits.remainingUsd} credit left in the org${data.spend.images ? ` · ${data.spend.images} images` : ''}${data.spend.seconds ? ` · ${data.spend.seconds}s video` : ''}` : `${data.spend.tokens ? `${(data.spend.tokens / 1000).toFixed(0)}k tokens` : 'no model calls yet'}`} />
        )}
        {inst && (
          <StripCell k="Native apps" tone={!data.native?.bundles?.length ? 'off' : !data.native.last ? 'warn' : data.native.last.status === 'done' ? 'ok' : ['failed', 'partial'].includes(data.native.last.status) ? 'down' : 'warn'}
            v={!data.native?.bundles?.length ? '—' : data.native.last ? `${data.native.last.status} · ${data.native.last.requested.join(', ')}` : 'no builds yet'}
            sub={data.native?.bundles?.length ? `${data.native.bundles.join(' + ')} · ${data.native.last ? fmtAgo(data.native.last.at) : 'Surfaces → Build'}` : 'Desktop / Mobile add-on'} />
        )}
        <StripCell k="Lab" tone={!data.lab?.runs ? 'off' : data.lab.awaiting ? 'warn' : (data.lab.lastTest?.verdict === 'fail' || data.lab.lastPerf?.verdict === 'fail' || data.lab.stories?.failing) ? 'down' : 'ok'}
          v={!data.lab?.runs ? '—' : data.lab.awaiting ? `${data.lab.awaiting} awaiting approval` : data.lab.stories?.total ? `${data.lab.stories.verified}/${data.lab.stories.total - (data.lab.stories.proposed || 0)} stories verified` : data.lab.lastTest ? `${data.lab.lastTest.verdict === 'pass' ? 'test passed' : 'test failed'} · ${data.lab.lastTest.env || ''}` : `${data.lab.runs} run${data.lab.runs === 1 ? '' : 's'}`}
          sub={!data.lab?.runs ? 'Lab add-on · research, refine, test' : data.lab.active ? `${data.lab.active} running` : data.lab.lastPerf ? `perf ${data.lab.lastPerf.verdict === 'pass' ? 'within budget' : 'over budget'} · ${fmtAgo(data.lab.lastPerf.at)}` : data.lab.lastTest ? `last test ${data.lab.lastTest.verdict} · ${fmtAgo(data.lab.lastTest.at)}` : data.lab.unappliedProposals ? `${data.lab.unappliedProposals} proposal(s) to apply` : ''} />
        {data.lab?.roadmap && (() => {
          const rm = data.lab.roadmap;
          return (
            <StripCell k="Roadmap" tone={rm.status === 'done' ? 'ok' : rm.status === 'failed' ? 'down' : rm.status === 'paused' || rm.status === 'proposed' ? 'warn' : 'ok'}
              v={rm.status === 'proposed' ? `plan proposed · ${rm.stories} stories` : rm.status === 'done' ? `done · ${rm.shipped}/${rm.stories} shipped to UAT` : `wave ${rm.wave}/${rm.waves} · ${rm.shipped}/${rm.stories} shipped`}
              sub={rm.status === 'proposed' ? 'approve on Lab → Stories → Roadmap' : rm.status === 'paused' ? `paused${rm.failed ? ` · ${rm.failed} failed` : ''}` : rm.status === 'done' ? 'promote to production when ready' : `${rm.building} building${rm.failed ? ` · ${rm.failed} failed` : ''} · ${fmtAgo(rm.at)}`} />
          );
        })()}
        {inst && (
          <StripCell k="Security" tone={!inst.assurance ? 'off' : open.length ? ((counts.critical || counts.high) ? 'down' : 'warn') : inst.scan?.lastScanAt ? 'ok' : 'warn'}
            v={!inst.assurance ? '—' : open.length ? open.map((k) => `${counts[k]} ${k}`).join(' · ') : inst.scan?.lastScanAt ? 'no open findings' : 'scan pending'}
            sub={inst.scan?.lastScanAt ? `scanned ${fmtAgo(inst.scan.lastScanAt)}` : !inst.assurance ? 'Assurance add-on' : ''} />
        )}
      </div>
      <div className="ab-strip-actions">
        {urgent.length > 0 && tab !== 'operate' && (
          <button type="button" className="cs-btn cs-btn-sm cs-btn-outline ab-strip-alert" onClick={() => onTab('operate')}>
            {urgent.length} thing{urgent.length === 1 ? '' : 's'} need{urgent.length === 1 ? 's' : ''} attention →
          </button>
        )}
        {tab !== 'operate' && <button type="button" className="cs-btn cs-btn-sm cs-btn-outline" onClick={() => onTab('operate')}>Operate</button>}
        {inst?.url && <a className="cs-btn cs-btn-sm cs-btn-ghost" href={inst.url} target="_blank" rel="noreferrer">Open app ↗</a>}
      </div>
    </div>
  );
}

function StripCell({ k, v, sub, tone = 'off' }) {
  return (
    <div className="ab-strip-cell">
      <span className="ab-strip-k">{k}</span>
      <span className="ab-strip-v"><i className={`ab-dot is-${tone}`} aria-hidden="true" />{v}</span>
      {sub ? <span className="ab-strip-sub">{sub}</span> : null}
    </div>
  );
}

// ---- Suggestions (the improvement loop) ------------------------------------------
// `only` narrows by target tab so Design/Knowledge show their own items.
export function Suggestions({ org, app, data, uid, only = null, onTab, compact = false, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const items = (data?.suggestions || []).filter((s) => !only || (s.action?.tab && only.includes(s.action.tab)));
  if (!data) return compact ? null : <p className="cs-help">Loading…</p>;
  if (!items.length) {
    return compact ? null : <p className="cs-help" style={{ margin: 0 }}>Nothing needs attention right now.</p>;
  }
  const startDoc = async (s) => {
    setBusy(s.id); setErr('');
    try {
      await createEntity('knowledgeDoc', { org: org.id }, {
        title: s.action.draft.title.slice(0, 200), content: '', sourceType: 'paste', audience: 'public', tags: ['from-conversations'],
        appIds: [app.id], createdBy: uid,
      });
      onChanged?.();
      navigate(withApp(`/orgs/${org.id}/knowledge`, app));
    } catch (e) { setErr(friendlyError(e)); } finally { setBusy(''); }
  };
  return (
    <ul className="mf-list ab-sugg">
      {items.map((s) => (
        <li key={s.id} className="mf-row">
          <Chip tone={s.severity === 'low' ? 'info' : s.severity === 'medium' ? 'warn' : 'danger'}>{s.severity}</Chip>
          <span className="mf-row-grow">{s.text}</span>
          {s.action?.draft
            ? <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy === s.id} onClick={() => startDoc(s)}>{busy === s.id ? 'Creating…' : 'Start a document'}</button>
            : s.action?.tab
              ? <button type="button" className="mf-muted cs-btn-link" onClick={() => onTab(s.action.tab, s.action.phase ? { phase: s.action.phase, card: s.action.card || null, id: s.action.runId || s.action.conceptId || null } : undefined)}>{s.action.catchup ? 'Catch up from the build' : `${TAB_LABEL[s.action.tab] || s.action.tab}${s.action.phase ? ` · ${PHASE_LABEL[s.action.phase] || s.action.phase}` : ''}`} →</button>
              : s.action?.href
                ? <Link to={`/orgs/${org.id}/${s.action.href}`} className="mf-muted">open →</Link>
                : null}
        </li>
      ))}
      {err && <li className="cs-help" style={{ color: 'var(--danger)' }}>{err}</li>}
    </ul>
  );
}
// Old tab ids still arrive from older suggestions / map nodes; they open the
// Lab phase that absorbed them (AppBuilderModule rewrites the URL).
const TAB_LABEL = { design: 'Lab · Design', knowledge: 'Lab · Design · documents', data: 'Lab · Design · data', policies: 'Lab · Design · policies', surfaces: 'Lab · Surfaces', preview: 'Lab · Try', lab: 'Lab', ship: 'Ready to ship', operate: 'Operate' };
const PHASE_LABEL = Object.fromEntries(LAB_PHASES.map((p) => [p.id, p.label]));

// ---- The app map --------------------------------------------------------------------
// Five columns, nodes coloured by state, connectors drawn after layout.
// onNode (optional): the Lab's Integration map intercepts clicks to open a
// drawer instead of jumping tabs; badge(nodeId) → { tone, label } adds a
// small chip per node (tested / failing / untested).
export function AppMap({ data, onTab, org, app, onNode = null, badge = null }) {
  const ref = useRef(null);
  const [paths, setPaths] = useState([]);
  const agents = data?.agents || [];
  const docs = data?.docs || [];
  const inst = data?.instance;
  const twin = data?.twin;
  const d = data?.data || {};
  // How many instances the org already has — "Not linked" reads very
  // differently when there are seven of them waiting in Deployments.
  const { docs: orgInstances } = useEntityList('appInstance', { org: org.id }, { orderBy: null, enabled: !inst });
  const linkable = (orgInstances || []).filter((i) => i.envRole !== 'uat' && !['terminated', 'consumed'].includes(i.status) && !/^pool-/.test(i.slug || '')).length;
  const showDocs = docs.slice(0, 8);
  const moreDocs = docs.length - showDocs.length;
  const dataNodes = [
    { id: 'data-integrations', label: 'Integrations', sub: `${d.integrationIds?.length || 0} linked`, tone: d.integrationIds?.length ? 'ok' : 'off', tab: 'data' },
    { id: 'data-sources', label: 'Data sources', sub: d.dataSourceIds?.length ? `${d.dataSourceIds.length} linked` : 'none', tone: d.dataSourceIds?.length ? 'ok' : 'off', tab: 'data' },
    { id: 'data-graphql', label: 'GraphQL', sub: d.graphqlReady ? 'ready' : 'not set up', tone: d.graphqlReady ? 'ok' : 'off', tab: 'data' },
    { id: 'data-workflow', label: 'Workflow',
      sub: !d.pipelineId ? 'none' : d.pipelineRequirements?.total ? `${d.pipelineRequirements.met}/${d.pipelineRequirements.total} requirements met` : 'linked',
      tone: !d.pipelineId ? 'off' : d.pipelineRequirements?.unmet ? 'warn' : 'ok', tab: 'data' },
  ];
  const anyAgent = agents.some((a) => a.enabled);
  const surfaceNodes = [
    { id: 'surf-web', label: 'Website script', sub: anyAgent ? (agents.some((a) => a.allowedOrigins?.length) ? 'allowed sites set' : 'any site') : 'needs an agent', tone: anyAgent ? 'ok' : 'off', tab: 'surfaces' },
    { id: 'surf-iframe', label: 'Iframe', sub: anyAgent ? 'ready' : 'needs an agent', tone: anyAgent ? 'ok' : 'off', tab: 'surfaces' },
    { id: 'surf-api', label: 'API', sub: anyAgent ? 'ready' : 'needs an agent', tone: anyAgent ? 'ok' : 'off', tab: 'surfaces' },
    { id: 'surf-host', label: 'Hosted app', sub: inst ? `${inst.slug} · ${inst.status}` : 'not linked', tone: inst ? (inst.status === 'live' ? stateTone(inst.monitor) : 'warn') : 'off', tab: 'surfaces' },
  ];
  const lastDeploy = (data?.deploys || []).find((x) => !(x.targets || []).includes('app-bundle')) || null;
  const instNodes = inst ? [
    { id: 'inst-prod', label: 'Production', sub: inst.monitor?.state ? `${inst.monitor.state}${inst.monitor.lastLatencyMs != null ? ` · ${inst.monitor.lastLatencyMs}ms` : ''}${servicesLabel(inst.services)}` : inst.status, tone: inst.status === 'live' ? instTone(inst) : 'warn', tab: 'operate' },
    { id: 'inst-uat', label: 'UAT twin', sub: twin ? (twin.monitor?.state || twin.status) : 'none', tone: twin ? stateTone(twin.monitor) : 'off', tab: 'operate' },
    { id: 'inst-tls', label: 'TLS', sub: inst.monitor?.tlsDaysLeft != null ? `${inst.monitor.tlsDaysLeft}d left` : '—', tone: inst.monitor?.tlsDaysLeft == null ? 'off' : inst.monitor.tlsDaysLeft < 7 ? 'down' : inst.monitor.tlsDaysLeft < 14 ? 'warn' : 'ok', tab: 'operate' },
    { id: 'inst-sec', label: 'Security', sub: !inst.assurance ? 'not enabled' : inst.scan?.lastScanAt ? (Object.values(inst.scan.counts || {}).some(Boolean) ? 'findings open' : 'clean') : 'scan pending', tone: !inst.assurance ? 'off' : (inst.scan?.counts?.critical || inst.scan?.counts?.high) ? 'down' : inst.scan?.counts?.medium ? 'warn' : inst.scan?.lastScanAt ? 'ok' : 'warn', tab: 'operate' },
    { id: 'inst-deploy', label: 'Last deploy', sub: lastDeploy ? `${lastDeploy.env} · ${fmtAgo(lastDeploy.at)}` : 'none', tone: lastDeploy ? (lastDeploy.status === 'done' ? 'ok' : lastDeploy.status === 'error' ? 'down' : 'warn') : 'off', tab: 'operate' },
    { id: 'inst-sync', label: 'Design sync', sub: data.sync ? (data.sync.drift ? 'behind' : fmtAgo(data.sync.at)) : 'never', tone: data.sync ? (data.sync.drift ? 'warn' : 'ok') : 'warn', tab: 'surfaces' },
  ] : [];

  // Edges by node id; drawn from the right edge of `from` to the left of `to`.
  const edges = [];
  for (const a of agents) {
    const aid = `agent-${a.id}`;
    const mode = a.knowledge?.mode || 'all';
    for (const doc of showDocs) {
      if (mode === 'docs' ? (a.knowledge.docIds || []).includes(doc.id) : true) edges.push([aid, `doc-${doc.id}`, doc.status === 'live' ? 'ok' : 'off']);
    }
    if (a.allowReads?.length || a.allowWrites?.length) edges.push([aid, 'data-graphql', 'ok']);
    if (d.integrationIds?.length) edges.push([aid, 'data-integrations', 'ok']);
    if (d.pipelineId) edges.push([aid, 'data-workflow', 'ok']);
    if (a.enabled) { edges.push([aid, 'surf-web', 'ok']); edges.push([aid, 'surf-iframe', 'ok']); edges.push([aid, 'surf-api', 'ok']); }
    if (inst) edges.push([aid, 'surf-host', data.sync ? (data.sync.drift ? 'warn' : 'ok') : 'off']);
  }
  // One node per service the kit phoned home; wired from Production.
  const svcNodes = (inst?.services?.list || []).map((s) => ({
    id: `svc-${s.name.replace(/[^a-z0-9]/gi, '-')}`, label: s.name,
    sub: inst.services.stale ? `as of ${fmtAgo(inst.services.at)}` : (s.state !== 'running' && s.exitCode === 0 ? 'done' : s.status || s.state),
    tone: LEVEL_TONE[s.level] || 'off', tab: 'operate',
  }));
  for (const n of instNodes) edges.push(['surf-host', n.id, n.tone === 'off' ? 'off' : n.tone]);
  for (const n of svcNodes) edges.push(['inst-prod', n.id, n.tone === 'off' ? 'off' : n.tone]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const draw = () => {
      const box = el.getBoundingClientRect();
      const pos = (id) => {
        const n = el.querySelector(`[data-node="${id}"]`);
        if (!n) return null;
        const r = n.getBoundingClientRect();
        return { l: r.left - box.left, r: r.right - box.left, y: r.top - box.top + r.height / 2 };
      };
      const out = [];
      for (const [from, to, tone] of edges) {
        const a = pos(from); const b = pos(to);
        if (!a || !b) continue;
        const dx = Math.max(24, (b.l - a.r) / 2);
        out.push({ key: `${from}>${to}`, tone, d: `M${a.r},${a.y} C${a.r + dx},${a.y} ${b.l - dx},${b.y} ${b.l},${b.y}` });
      }
      setPaths(out);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(edges), showDocs.length, instNodes.length, svcNodes.length]);

  const Node = ({ id, label, sub, tone = 'off', tab, href }) => {
    const b = badge ? badge(id) : null;
    const inner = (<><i className={`ab-dot is-${tone}`} aria-hidden="true" /><span className="ab-node-text"><strong>{label}</strong>{sub && <span>{sub}</span>}</span>{b ? <span className={`mf-chip is-${b.tone} ab-badge`}>{b.label}</span> : null}</>);
    const cls = `ab-node is-${tone}`;
    if (href && !onNode) return <a className={cls} data-node={id} href={href} target="_blank" rel="noreferrer">{inner}</a>;
    return <button type="button" className={cls} data-node={id} onClick={() => (onNode ? onNode({ id, label, sub, tone, tab, href }) : tab && onTab(tab))}>{inner}</button>;
  };

  return (
    <div className="ab-map-scroll">
    <div className="ab-map" ref={ref}>
      <svg className="ab-map-wires" aria-hidden="true">
        {paths.map((p) => <path key={p.key} d={p.d} className={`is-${p.tone}`} />)}
      </svg>
      <div className="ab-map-col">
        <h3>Agents</h3>
        {agents.map((a) => <Node key={a.id} id={`agent-${a.id}`} label={a.name || 'Agent'} sub={a.enabled ? (a.knowledge?.mode === 'docs' ? `${a.knowledge.docIds?.length || 0} documents` : a.knowledge?.mode === 'tags' ? 'by tags' : 'all published') : 'disabled'} tone={a.enabled ? (a.hasToken ? 'ok' : 'warn') : 'off'} tab="design" />)}
        {!agents.length && <Node id="agent-none" label="No agent" sub="attach one" tone="down" tab="design" />}
      </div>
      <div className="ab-map-col">
        <h3>Knowledge</h3>
        {showDocs.map((doc) => <Node key={doc.id} id={`doc-${doc.id}`} label={doc.title} sub={`${doc.status} · ${doc.audience}`} tone={doc.status === 'live' ? (doc.audience === 'public' ? 'ok' : 'info') : 'off'} tab="knowledge" />)}
        {moreDocs > 0 && <Node id="doc-more" label={`+${moreDocs} more`} sub="" tone="info" tab="knowledge" />}
        {!docs.length && <Node id="doc-none" label="No documents" sub="add some" tone="down" tab="knowledge" />}
      </div>
      <div className="ab-map-col">
        <h3>Data & actions</h3>
        {dataNodes.map((n) => <Node key={n.id} {...n} />)}
      </div>
      <div className="ab-map-col">
        <h3>Surfaces</h3>
        {surfaceNodes.map((n) => <Node key={n.id} {...n} />)}
      </div>
      <div className="ab-map-col">
        <h3>Hosted instance</h3>
        {instNodes.map((n) => <Node key={n.id} {...n} />)}
        {svcNodes.length > 0 && (
          <>
            <h3 className="ab-map-sub">Services{inst.services.stale ? ' · unknown' : ` · ${inst.services.up}/${inst.services.total} up`}<span className="mf-muted"> · {fmtAgo(inst.services.at)}</span></h3>
            {svcNodes.map((n) => <Node key={n.id} {...n} />)}
          </>
        )}
        {!inst && <Node id="inst-none" label="Not linked" sub={linkable ? `${linkable} instance${linkable === 1 ? '' : 's'} in this org — pick one under Surfaces` : 'App Builder → Surfaces'} tone={linkable ? 'warn' : 'off'} tab="surfaces" />}
      </div>
    </div>
    </div>
  );
}

// ---- Operate tab -------------------------------------------------------------------
export function OperateTab({ org, app, data, err, uid, isAdmin, canEdit = isAdmin, onTab, reload }) {
  const inst = data?.instance;
  const conv = data?.conversations;
  if (err) return <p className="cs-help">Operations feed unavailable: {err}</p>;
  if (!data) return <p className="cs-help">Loading the app's picture…</p>;
  const agentName = (id) => data.agents.find((a) => a.id === id)?.name || id;
  const bundleDeploys = (data.deploys || []).filter((x) => (x.targets || []).includes('app-bundle'));
  const codeDeploys = (data.deploys || []).filter((x) => !(x.targets || []).includes('app-bundle'));
  return (
    <>
      <ModuleFrame.Panel title="The whole app" meta="click any part to go there · colours are live state">
        <AppMap data={data} onTab={onTab} org={org} app={app} />
      </ModuleFrame.Panel>

      <ModuleFrame.Columns cols={2}>
        <ModuleFrame.Panel title="Needs attention" meta={`${data.suggestions.length} suggestion${data.suggestions.length === 1 ? '' : 's'} from live signals`}>
          <Suggestions org={org} app={app} data={data} uid={uid} onTab={onTab} onChanged={reload} />
        </ModuleFrame.Panel>
        <ModuleFrame.Panel title={`Conversations · last ${conv.days} days`} meta={`${conv.sessions} sessions · ${conv.turns} turns · ${conv.proposals} proposals`}>
          {!conv.sessions && <p className="cs-help" style={{ margin: 0 }}>{data.agents.length ? 'No one has talked to this app yet in this window. Put the assistant on a website (Surfaces) or try it in Preview.' : 'Attach an agent first (Design).'}</p>}
          {conv.sessions > 0 && (
            <>
              <KV rows={[
                ...Object.entries(conv.byAgent).map(([id, a]) => [agentName(id), `${a.sessions} sessions · ${a.turns} turns${a.gaps ? ` · ${a.gaps} unanswered` : ''}`]),
                ['Where from', conv.origins.map((o) => `${o.host} (${o.n})`).join(' · ') || '—'],
              ]} />
              {conv.recent.length > 0 && (
                <ul className="mf-list" style={{ marginTop: 10 }}>
                  {conv.recent.map((s) => (
                    <li key={s.id} className="mf-row">
                      <span className="mf-row-grow">
                        {s.firstQuestion || <em>(no question yet)</em>}
                        <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{agentName(s.agentId)} · {s.origin} · {s.turns} turn{s.turns === 1 ? '' : 's'} · {fmtAgo(s.at)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {conv.unanswered > 0 && (
            <p className="cs-help" style={{ marginTop: 10 }}>{conv.unanswered} question{conv.unanswered === 1 ? '' : 's'} came back without a source — the ones asked most are listed under “Needs attention” with a one-click draft document.</p>
          )}
        </ModuleFrame.Panel>
      </ModuleFrame.Columns>

      <ModuleFrame.Columns cols={2}>
        <ModuleFrame.Panel title="Review queue" meta={`${data.review.pending} waiting`} actions={<Link to={`/orgs/${org.id}/human-review?app=${app.id}`} className="cs-btn cs-btn-outline cs-btn-sm">Open Human Review</Link>}>
          {!data.review.recent.length && <p className="cs-help" style={{ margin: 0 }}>Nothing has needed a person yet.</p>}
          <ul className="mf-list">
            {data.review.recent.map((r) => (
              <li key={r.id} className="mf-row">
                <Chip tone={r.status === 'pending' ? 'warn' : r.status === 'approved' ? 'ok' : 'muted'}>{r.status}</Chip>
                <span className="mf-row-grow">{r.question || '(no question)'}<span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{r.reason} · {r.queue} · {fmtAgo(r.createdAt)}</span></span>
              </li>
            ))}
          </ul>
        </ModuleFrame.Panel>
        <ModuleFrame.Panel title="Releases & syncs" meta={inst ? `${codeDeploys.length} deploys · ${bundleDeploys.length} design syncs` : 'no hosted instance'} actions={<Link to={withApp(`/orgs/${org.id}/deployments`, app)} className="cs-btn cs-btn-outline cs-btn-sm">Deployments</Link>}>
          {!inst && <p className="cs-help" style={{ margin: 0 }}>Link a hosted instance under Surfaces to see releases here. Website, iframe and API surfaces are live the moment an agent is enabled.</p>}
          <ul className="mf-list">
            {(data.deploys || []).slice(0, 8).map((x) => (
              <li key={x.id} className="mf-row">
                <Chip tone={x.status === 'done' ? 'ok' : x.status === 'error' ? 'danger' : 'warn'}>{x.status}</Chip>
                <span className="mf-row-grow">
                  {(x.targets || []).includes('app-bundle') ? `design sync · ${x.message}` : `${x.env} deploy${x.headSha ? ` · ${x.headSha}` : ''}${x.stage && x.status !== 'done' ? ` · ${x.stage}` : ''}`}
                  <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{fmtAgo(x.at)}{x.requestedBy ? ` · ${x.requestedBy}` : ''}</span>
                </span>
              </li>
            ))}
            {(data.builds || []).map((b) => (
              <li key={b.sessionId} className="mf-row">
                <Chip tone={b.state === 'shipped' ? 'ok' : b.state === 'built' ? 'ok' : 'info'}>{b.state === 'underway' ? 'building' : b.state}</Chip>
                <span className="mf-row-grow">
                  {b.scope === 'first' ? 'first build' : `build · ${(b.titles || []).join(' · ') || `${b.storyIds?.length || 0} stor${b.storyIds?.length === 1 ? 'y' : 'ies'}${b.screenIds?.length ? `, ${b.screenIds.length} screen(s)` : ''}`}`}
                  {b.prUrl ? <> · <a href={b.prUrl} target="_blank" rel="noreferrer" className="mf-muted">pull request ↗</a></> : null}
                  <span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{fmtAgo(b.shippedAt || b.deployedAt || b.startedAt)}{b.deployEnv ? ` · ${b.deployEnv}` : ''}{b.requestedBy ? ` · ${b.requestedBy}` : ''}</span>
                </span>
              </li>
            ))}
            {data.releases.map((r) => (
              <li key={r.id} className="mf-row">
                <Chip tone={r.status === 'released' ? 'ok' : r.status === 'approved' ? 'info' : 'muted'}>{r.status}</Chip>
                <span className="mf-row-grow">release {r.version || r.id}<span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{fmtAgo(r.createdAt)}</span></span>
              </li>
            ))}
            {data.qa.map((q) => (
              <li key={q.id} className="mf-row">
                <Chip tone={q.status === 'passed' ? 'ok' : q.status === 'failed' ? 'danger' : 'muted'}>QA {q.status}</Chip>
                <span className="mf-row-grow">{q.passed != null ? `${q.passed} passed${q.failed ? ` · ${q.failed} failed` : ''}` : 'test run'}<span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{fmtAgo(q.createdAt)}</span></span>
              </li>
            ))}
          </ul>
        </ModuleFrame.Panel>
      </ModuleFrame.Columns>

      {inst && inst.status === 'live' && (
        <div className="ab-inst-sections">
          <ApSection id="ab-eco" title="Live picture" className="ap-eco" meta="the instance as it runs — services, monitor, security, twin; the service list is under Services" defaultOpen>
            <EcosystemSection orgId={org.id} appId={inst.id} />
          </ApSection>
          <ApSection id="ab-health" title="Health" meta="uptime, latency, TLS, your feature checks">
            <HealthSection orgId={org.id} appId={inst.id} canEdit={canEdit} />
          </ApSection>
          <ApSection id="ab-updates" title="Updates" meta={updatesMeta(inst)} defaultOpen={inst.monitor?.versionBehind === true || inst.updates?.auto === false}>
            <UpdatesSection orgId={org.id} appId={inst.id} isAdmin={isAdmin} />
          </ApSection>
          <ApSection id="ab-security" title="Security" meta={inst.assurance ? 'scans · findings ledger' : 'Assurance add-on'} defaultOpen={false}>
            <SecuritySection orgId={org.id} appId={inst.id} isAdmin={isAdmin} />
          </ApSection>
          {inst.monitor?.brain && (
            <ApSection id="ab-brain" title="Brain" meta={brainMeta(inst.monitor.brain)} defaultOpen={false}>
              {/* Three verdicts phoned home by the instance (kit shared/brainView.js brainLiveness): populated · updating · current. The full, role-filtered brain lives on the app at /brain. */}
              {inst.monitor.brain.verdicts && (
                <div className="ab-verdicts" data-nova-note="The app's brain: populated = pages exist and lint clean; updating = a dated log entry in the last 30 days and few inbox drafts; current = the assistant's corpus was seeded from the running kit and is indexed.">
                  {['populated', 'updating', 'current'].map((k) => {
                    const v = inst.monitor.brain.verdicts[k];
                    if (!v) return null;
                    return <span key={k} className="ab-verdict"><Chip tone={v.level === 'ok' ? 'ok' : v.level === 'down' ? 'danger' : 'warn'}>{k}</Chip><span className="mf-muted">{v.reason}</span></span>;
                  })}
                </div>
              )}
              {inst.url && (
                <p className="cs-help">
                  <a className="cs-btn cs-btn-sm" href={`${String(inst.url).replace(/\/$/, '')}/brain`} target="_blank" rel="noreferrer">Open this app's brain</a>
                  <span className="mf-muted" style={{ marginLeft: 8 }}>knowledge, design, technical reference and data counts — filtered by your seat on the app</span>
                </p>
              )}
              {inst.monitor.brain.entries?.length ? (
                <ul className="mf-list" data-nova-note="The app's own engineering log, written by the agents and people who change its code. Lessons and decisions are indexed into the app's assistant for its operators.">
                  {inst.monitor.brain.entries.map((e, i) => (
                    <li key={`${e.date}-${i}`} className="mf-row">
                      <Chip tone={e.type === 'lesson' ? 'warn' : e.type === 'decision' ? 'info' : 'muted'}>{e.type}</Chip>
                      <span className="mf-row-grow">{e.title}<span className="mf-muted" style={{ display: 'block', fontSize: 12 }}>{e.date}</span></span>
                    </li>
                  ))}
                </ul>
              ) : <p className="cs-help">No sessions logged yet — the log fills as the app's code changes.</p>}
              {/* The runtime side of the app's brain: drafts waiting in its inbox (self-test flips, notes this plane sent) and whether the assistant's corpus matches the running kit. */}
              {(inst.monitor.brain.inboxPending || inst.monitor.brain.corpusKitRef) && (
                <p className="cs-help" data-nova-note="inboxPending: drafts the instance filed for its own brain, drained by the app's engineers. corpusKitRef: the kit build that seeded the assistant's knowledge; stale when it differs from the running kit.">
                  {inst.monitor.brain.inboxPending ? <>{inst.monitor.brain.inboxPending} draft{inst.monitor.brain.inboxPending === 1 ? '' : 's'} waiting in the app's brain inbox. </> : null}
                  {inst.monitor.brain.corpusKitRef ? (
                    inst.monitor.kitRef && !sameRef(inst.monitor.brain.corpusKitRef, inst.monitor.kitRef)
                      ? <Chip tone="warn">assistant corpus seeded from {String(inst.monitor.brain.corpusKitRef).slice(0, 12)} — running {String(inst.monitor.kitRef).slice(0, 12)}; re-run the knowledge seed</Chip>
                      : <>Assistant corpus matches the running kit ({String(inst.monitor.brain.corpusKitRef).slice(0, 12)}).</>
                  ) : null}
                </p>
              )}
              {inst.devUrl && <p className="cs-help">Full pages live in the app's repo under <code>brain/</code>; read them in the <a href={inst.devUrl} target="_blank" rel="noreferrer">Dev Console</a>.</p>}
            </ApSection>
          )}
          {inst.monitor?.telephony && (
            <ApSection id="ab-phone" title="Phone" meta={phoneMeta(inst.monitor.telephony)} defaultOpen={inst.monitor.telephony.enabled && !inst.monitor.telephony.ready}>
              <PhoneSection orgId={org.id} appId={inst.id} monitor={inst.monitor.telephony} />
            </ApSection>
          )}
          {inst.devUrl && (
            <ApSection id="ab-devaccess" title="Dev Console access" meta="change the app's code with agents · one-click sign-in from nventr.io" defaultOpen={false}>
              <DevAccessSection orgId={org.id} appId={inst.id} app={{ urls: { app: inst.url, dev: inst.devUrl }, slug: inst.slug, devAccess: inst.devAccess || null }} />
            </ApSection>
          )}
          <CostsSection orgId={org.id} appId={inst.id} />
          <p className="cs-help">Full instance record (provisioning, usage, invoices, agreements): <Link to={`/orgs/${org.id}/apps/${inst.id}`}>open {inst.slug} →</Link></p>
        </div>
      )}
    </>
  );
}
